import { useQuery, useMutation } from "@apollo/client";
import { BLOCKED_TIMES_BY_STUDIO_ID } from "../../graphql/queries/calendarQueries";
import { CREATE_BLOCKED_TIME, DELETE_BLOCKED_TIME } from "../../graphql/mutations/calendarMutations";
import { useState } from "react";

export const useBlockedTimes = (studioId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sanitizeInput = (input = {}) => {
    const cleaned = { ...input };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Query to fetch blocked times for a specific studio
  const { 
    data: blockedTimesData, 
    loading: blockedTimesLoading, 
    error: blockedTimesError,
    refetch: refetchBlockedTimes
  } = useQuery(BLOCKED_TIMES_BY_STUDIO_ID, {
    variables: {
      studioID: studioId
    },
    skip: !studioId
  });

  // Mutation to create a new blocked time
  const [createBlockedTimeMutation] = useMutation(CREATE_BLOCKED_TIME, {
    onError: (err) => {
      setError(err);
      console.error("Error creating blocked time:", err);
    }
  });

  const [deleteBlockedTimeMutation] = useMutation(DELETE_BLOCKED_TIME, {
    onError: (err) => {
      setError(err);
    }
  });

  // Function to create a new blocked time
  const createBlockedTime = async (blockedTimeData = {}) => {
    try {
      setLoading(true);
      setError(null);

      if (!blockedTimeData?.studioID) {
        throw new Error("studioID is required to create a blocked time");
      }

      if (!blockedTimeData?.startDateTime || !blockedTimeData?.endDateTime) {
        throw new Error("startDateTime and endDateTime are required");
      }

      const input = sanitizeInput({
        studioID: blockedTimeData.studioID,
        title: blockedTimeData.title,
        startDateTime: blockedTimeData.startDateTime,
        endDateTime: blockedTimeData.endDateTime,
        isFullDay: blockedTimeData.isFullDay ?? false
      });

      const variables = sanitizeInput({
        input,
        condition: blockedTimeData?.condition
      });

      const result = await createBlockedTimeMutation({
        variables
      });

      if (studioId) {
        await refetchBlockedTimes();
      }

      return result.data?.createBlockedTime;
    } catch (err) {
      setError(err);
      console.error("Error creating blocked time:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a blocked time
  const deleteBlockedTime = async (blockedTime) => {
    try {
      setLoading(true);
      setError(null);

      const id = typeof blockedTime === "string" ? blockedTime : blockedTime?.id;
      if (!id) {
        throw new Error("Blocked time ID is required");
      }

      const variables = sanitizeInput({
        input: sanitizeInput({ id }),
        condition: typeof blockedTime === "object" ? blockedTime?.condition : undefined
      });

      const result = await deleteBlockedTimeMutation({
        variables
      });

      const deleted = result?.data?.deleteBlockedTime;

      if (studioId) {
        await refetchBlockedTimes();
      }

      return deleted;
    } catch (err) {
      setError(err);
      console.error("Error deleting blocked time:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Format blocked times for calendar display
  const formattedBlockedTimes = blockedTimesData?.blockedTimesByStudioID?.items?.map(blockedTime => {
    const startDate = new Date(blockedTime.startDateTime);
    const endDate = new Date(blockedTime.endDateTime);

    return {
      id: blockedTime.id,
      title: blockedTime.title || "",
      start: startDate,
      end: endDate,
      allDay: blockedTime.isFullDay,
      color: "#FF0000",
      textColor: "#FFFFFF"
    };
  }) || [];

  return {
    blockedTimes: blockedTimesData,
    loading: loading || blockedTimesLoading,
    error: error || blockedTimesError,
    createBlockedTime,
    deleteBlockedTime,
    refetch: refetchBlockedTimes,
    refetchBlockedTimes
  };
}; 